import { Injectable } from '@angular/core';
import { ApiClient } from './api-client';
import { WebSocketService } from '../services/web-socket.service';
import { Observable } from 'rxjs';
import { HighGuideFilter } from '../models/guide/high-guide-filter';
import { Endpoints } from './endpoints';
import { map } from 'rxjs/operators';
import { LoggingService } from '../services/logging-service';
import { Api } from './api';
import { CastToBsError } from './cast-to-bs-error';
import { DataSyncingService } from '../services/data-sync/data-syncing.service';
import { TrackApiCall } from '../services/data-sync/track-api-call';
import { HighGuideCompanySignals } from '../models/product/dto/product-signal/high-guide-company-signals';
import { HighGuideProductSignal } from '../models/product/dto/product-signal/high-guide-product-signal';
import { HighGuideInsights } from '../models/guide/dto/high-guide-insights';
import { Ranking } from '../models/product/dto/product-signal/ranking';
import { WebSocketRequestHighGuideCompanySignals } from '../models/web-sockets/messages/send/request/web-socket-request-high-guide-company-signals';

@Injectable() // Provided by Logged In Scope
export class GuideApi extends Api {
  constructor(
    private webSocketService: WebSocketService,
    apiClient: ApiClient,
    loggingService: LoggingService,
    dataSyncingService: DataSyncingService
  ) {
    super(apiClient, loggingService, dataSyncingService);
  }

  public serviceName = 'Guide';

  @TrackApiCall()
  @CastToBsError()
  public getFilters$(ids?: string[]): Observable<HighGuideFilter[]> {
    const url = Endpoints.getHighGuideFilters(ids);
    return this.apiClient.getArr$(HighGuideFilter, url);
  }

  /**
   * The signed-in user's own signals.
   */
  @TrackApiCall()
  @CastToBsError()
  public getProductSignals$(variantIds?: string[]): Observable<HighGuideProductSignal[]> {
    const url = Endpoints.getHighGuideProductSignals(variantIds);
    return this.apiClient.getArr$(HighGuideProductSignal, url);
  }

  /**
   * Location scoped. The active location travels in the `LocationId` header rather than the query string, matching
   * how the HighGuide service expects it.
   */
  @TrackApiCall()
  @CastToBsError()
  public getProductRankings$(locationId: number): Observable<Ranking[]> {
    const additionalHeaders = { LocationId: locationId?.toString() };
    const url = Endpoints.getHighGuideProductRankings();
    return this.apiClient.getArr$(Ranking, url, additionalHeaders);
  }

  /**
   * The home page insight rollup - trending brands, recent recommendations and insight collection results in one
   * read. Location scoped through the `LocationId` header, matching `getProductRankings$`.
   *
   * `windowDays` is left to the backend default when omitted; the response reports the window it actually used.
   */
  @TrackApiCall()
  @CastToBsError()
  public getInsights$(locationId: number, windowDays?: number): Observable<HighGuideInsights> {
    const additionalHeaders = { LocationId: locationId?.toString() };
    const url = Endpoints.getHighGuideInsights(windowDays);
    return this.apiClient.getObj$(HighGuideInsights, url, additionalHeaders);
  }

  /**
   * Every teammate's signals. Websocket only - there is no REST equivalent, so unlike the reads above there is no
   * fallback and errors propagate to the caller.
   *
   * Must be `requestObject$` rather than `requestMap$`: the response is a wrapper object, and `requestMap$` would
   * deserialize the whole body as `Map<string, HighGuideCompanySignals>`. The nested `Map<variantId, signals[]>` is
   * rehydrated by `HighGuideCompanySignals.onDeserialize` instead.
   */
  @TrackApiCall()
  @CastToBsError()
  public getCompanySignals$(
    userId: string,
    companyId: number,
    locationId: number
  ): Observable<HighGuideCompanySignals> {
    const msg = new WebSocketRequestHighGuideCompanySignals(userId, companyId, locationId);
    return this.webSocketService.requestObject$(msg, HighGuideCompanySignals).pipe(map(({ data }) => data));
  }
}
